import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import OpportunityForm from '@/modules/crm/components/OpportunityForm';
import { useCreateOpportunity, useCrmContact } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';

export default function CrmNewOpportunityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toasts, showToast, hideToast } = useToast();
  
  const contactId = searchParams.get('contactId');
  
  // If contactId is provided, fetch contact details for display
  const { data: contact } = useCrmContact(contactId);
  
  const createMutation = useCreateOpportunity();

  const handleSubmit = (formData) => {
    createMutation.mutate(
      { ...formData, contactId: formData.contactId || contactId },
      {
        onSuccess: (data) => {
          showToast('Opportunity created', 'success');
          navigate(`/crm/opportunities/${data.id}`);
        },
        onError: (error) => {
          showToast(error.message || 'Failed to create opportunity', 'error');
        },
      }
    );
  };

  const getContactName = () => {
    if (!contact) return null;
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Back Button */}
      <button
        onClick={() => navigate(contactId ? `/crm/contacts/${contactId}` : '/crm/opportunities')}
        className="inline-flex items-center text-zapt-purple hover:text-zapt-purple/80 font-medium mb-6"
      >
        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back
      </button>

      <div className="max-w-2xl">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">New Opportunity</h1>
          {contact && (
            <p className="text-gray-600 mb-6">
              For contact: <span className="font-medium">{getContactName()}</span>
              {contact.company && ` at ${contact.company}`}
            </p>
          )}
          
          <OpportunityForm
            preselectedContactId={contactId}
            preselectedContactName={getContactName()}
            onSubmit={handleSubmit}
            onCancel={() => navigate(contactId ? `/crm/contacts/${contactId}` : '/crm/opportunities')}
            isSubmitting={createMutation.isPending}
          />
        </div>
      </div>
    </CrmLayout>
  );
}
